import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  LogOut,
  LayoutDashboard,
  Sparkles,
  Building2,
  ShieldCheck,
  Gavel,
  ListChecks,
  Wrench,
  TrendingUp,
  FileEdit,
  ScrollText,
  Cpu,
  Cog,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import Crown from "./Crown";
import TutorialDrawer from "./TutorialDrawer";
import AccountSecurityDialog from "./AccountSecurityDialog";

const NAV_ITEMS = [
  { to: "/", end: true, label: "Command", icon: Gavel, testId: "nav-command", requires: null },
  { to: "/companies", label: "Companies", icon: Building2, testId: "nav-companies", requires: "any_admin" },
  { to: "/enterprise", label: "Registry", icon: LayoutDashboard, testId: "nav-registry", requires: "any_admin" },
  { to: "/enterprise/tasks", label: "Delegations", icon: ListChecks, testId: "nav-delegations", requires: "any_admin" },
  { to: "/approvals", label: "Approvals", icon: Gavel, testId: "nav-approvals", requires: "approvals.view" },
  { to: "/editor", label: "Site Editor", icon: FileEdit, testId: "nav-editor", requires: "any_admin" },
  { to: "/fix-center", label: "Fix Center", icon: Wrench, testId: "nav-fix", requires: "any_admin" },
  { to: "/kpi-risk", label: "KPI & Risk", icon: TrendingUp, testId: "nav-kpi", requires: "any_admin" },
  { to: "/admins", label: "People", icon: ShieldCheck, testId: "nav-admins", requires: "admins.view" },
  { to: "/vaelithon", label: "Vaelithon Reign", icon: Sparkles, testId: "nav-vaelithon", requires: "integrations.view" },
  { to: "/audit", label: "Audit", icon: ScrollText, testId: "nav-audit", requires: "audit.view" },
  { to: "/developer", label: "Developer", icon: Cpu, testId: "nav-developer", requires: "security.view" },
];

const canSee = (item, admin, hasPermission) => {
  if (!item.requires) return true;
  if (item.requires === "any_admin") return !!admin;
  return hasPermission(item.requires);
};

const Layout = ({ children }) => {
  const { user, admin, logout, hasPermission } = useAuth();
  const [securityOpen, setSecurityOpen] = React.useState(false);
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const visibleNav = NAV_ITEMS.filter((n) => canSee(n, admin, hasPermission));

  return (
    <div className="min-h-screen bg-obsidian text-textmain">
      <header
        data-testid="app-header"
        className="sticky top-0 z-40 backdrop-blur-xl bg-obsidian/70 border-b border-white/5"
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 group shrink-0" data-testid="brand-link">
            <Crown className="w-8 h-8" strokeWidth={1.2} />
            <div className="flex flex-col leading-none">
              <span className="font-heading text-xl tracking-wide text-textmain group-hover:text-champagne transition-colors">
                Aurethion <span className="wordmark-nexus text-[13px] ml-0.5">Crown</span>
              </span>
              <span className="label-eyebrow mt-1 text-[10px]">
                {admin ? "AI Orchestration · Intelligent Operations" : "AI Orchestration Console"}
              </span>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1 flex-1 justify-center overflow-x-auto scrollbar-hide">
            {visibleNav.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  data-testid={item.testId}
                  className={({ isActive }) =>
                    `px-2.5 py-1.5 rounded-md text-xs flex items-center gap-1.5 transition-colors whitespace-nowrap ${
                      isActive
                        ? "text-textmain bg-elevated border border-seam"
                        : "text-textmuted hover:text-textmain hover:bg-elevated"
                    }`
                  }
                >
                  <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                  {item.label}
                </NavLink>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            {admin && (
              <span
                data-testid="role-pill"
                className="hidden xl:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-champagne/30 bg-champagne/10 text-[10px] tracking-wider uppercase text-champagne"
                title={admin.role.replace(/_/g, " ")}
              >
                <Crown className="w-3 h-3" strokeWidth={1.6} />
                {admin.role === "owner_ceo" ? "CEO" : admin.role.replace(/_/g, " ")}
              </span>
            )}
            <button
              type="button"
              onClick={() => setSecurityOpen(true)}
              data-testid="open-account-security"
              title="Account security — change password"
              className="hidden sm:flex flex-col items-end leading-tight rounded-md px-2 py-1 -mx-2 -my-1 hover:bg-elevated/60 transition-colors text-left focus:outline-none focus:ring-1 focus:ring-champagne/40"
            >
              <span className="text-sm text-textmain font-medium" data-testid="user-name">
                {user?.name}
              </span>
              <span className="text-xs text-textmuted">{user?.email}</span>
            </button>
            <button
              type="button"
              onClick={handleLogout}
              data-testid="logout-btn"
              className="p-2 rounded-md border border-seam text-textmuted hover:text-champagne hover:border-champagne/40 transition-colors"
              title="Sign out"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <TutorialDrawer />
          </div>
        </div>

        {/* Mobile nav */}
        {visibleNav.length > 1 && (
          <nav className="md:hidden border-t border-white/5 bg-obsidian/80">
            <div className="flex overflow-x-auto px-3 py-2 gap-1">
              {visibleNav.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={`m-${item.to}`}
                    to={item.to}
                    end={item.end}
                    data-testid={`m-${item.testId}`}
                    className={({ isActive }) =>
                      `shrink-0 px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5 transition-colors ${
                        isActive
                          ? "text-textmain bg-elevated border border-seam"
                          : "text-textmuted hover:text-textmain hover:bg-elevated"
                      }`
                    }
                  >
                    <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                    {item.label}
                  </NavLink>
                );
              })}
            </div>
          </nav>
        )}
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 animate-fade-in">
        {children}
      </main>

      <footer className="border-t border-white/5 mt-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-textmuted">
          <div className="flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-champagne" strokeWidth={1.5} />
            <span>AI Orchestration · Intelligent Operations · Limitless Impact.</span>
          </div>
          <span>© {new Date().getFullYear()} Aurethion Crown. R.S.V.P. Enterprise OS.</span>
        </div>
      </footer>
      <AccountSecurityDialog open={securityOpen} onOpenChange={setSecurityOpen} />
    </div>
  );
};

export default Layout;
